import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';
import { fetchAdminSettings, updateAdminSetting } from '../lib/apiClient';
import { useAuth } from '../lib/auth';

type BrandingFormState = {
  productName: string;
  supportEmail: string;
  marketingSite: string;
};

type BillingFormState = {
  defaultPlan: string;
  trialLengthDays: number;
};

export function AdminSettingsPage(): JSX.Element {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isSuperAdmin = user?.role === 'superadmin';

  const { data, isLoading } = useQuery({
    queryKey: ['admin-settings'],
    queryFn: fetchAdminSettings,
    enabled: Boolean(isSuperAdmin),
  });

  const [branding, setBranding] = useState<BrandingFormState>({
    productName: '',
    supportEmail: '',
    marketingSite: '',
  });
  const [billing, setBilling] = useState<BillingFormState>({ defaultPlan: 'starter', trialLengthDays: 7 });

  useEffect(() => {
    if (data?.branding) {
      setBranding({
        productName: String(data.branding.productName ?? ''),
        supportEmail: String(data.branding.supportEmail ?? ''),
        marketingSite: String(data.branding.marketingSite ?? ''),
      });
    }
    if (data?.billing) {
      setBilling({
        defaultPlan: String(data.billing.defaultPlan ?? 'starter'),
        trialLengthDays: Number(data.billing.trialLengthDays ?? 7),
      });
    }
  }, [data]);

  const brandingMutation = useMutation({
    mutationFn: (payload: BrandingFormState) => updateAdminSetting('branding', payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-settings'] }),
  });

  const billingMutation = useMutation({
    mutationFn: (payload: BillingFormState) => updateAdminSetting('billing', payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-settings'] }),
  });

  const saving = brandingMutation.isPending || billingMutation.isPending;

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!isSuperAdmin) {
    return <Navigate to="/app" replace />;
  }

  const hasChanges = (() => {
    if (!data) return true;
    const brandingDirty =
      branding.productName !== (data.branding?.productName ?? '') ||
      branding.supportEmail !== (data.branding?.supportEmail ?? '') ||
      branding.marketingSite !== (data.branding?.marketingSite ?? '');
    const billingDirty =
      billing.defaultPlan !== (data.billing?.defaultPlan ?? 'starter') ||
      billing.trialLengthDays !== Number(data.billing?.trialLengthDays ?? 7);
    return brandingDirty || billingDirty;
  })();

  const handleSave = async () => {
    if (!data) return;
    await brandingMutation.mutateAsync(branding);
    await billingMutation.mutateAsync(billing);
  };

  return (
    <div className="space-y-6">
      <header>
        <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Super admin</p>
        <h1 className="text-2xl font-semibold text-foreground">Platform settings</h1>
        <p className="text-sm text-muted-foreground">Update global branding, contact, and billing metadata.</p>
      </header>

      <section className="glass-card space-y-6 p-6">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Branding</h2>
          <p className="text-sm text-muted-foreground">Information displayed across public marketing touchpoints.</p>
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {[...Array(3).keys()].map((key) => (
              <div key={key} className="h-12 animate-pulse rounded-xl bg-white/40 dark:bg-white/10" />
            ))}
          </div>
        ) : (
          <div className="grid gap-5 md:grid-cols-2">
            <label className="flex flex-col gap-2 text-sm font-medium text-foreground">
              Product name
              <input
                type="text"
                value={branding.productName}
                onChange={(event) => setBranding((prev) => ({ ...prev, productName: event.target.value }))}
                className="focus-ring rounded-xl border border-white/20 bg-white/60 px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground dark:bg-white/5"
              />
            </label>
            <label className="flex flex-col gap-2 text-sm font-medium text-foreground">
              Support email
              <input
                type="email"
                value={branding.supportEmail}
                onChange={(event) => setBranding((prev) => ({ ...prev, supportEmail: event.target.value }))}
                className="focus-ring rounded-xl border border-white/20 bg-white/60 px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground dark:bg-white/5"
              />
            </label>
            <label className="md:col-span-2 flex flex-col gap-2 text-sm font-medium text-foreground">
              Marketing site
              <input
                type="url"
                value={branding.marketingSite}
                onChange={(event) => setBranding((prev) => ({ ...prev, marketingSite: event.target.value }))}
                className="focus-ring rounded-xl border border-white/20 bg-white/60 px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground dark:bg-white/5"
              />
            </label>
          </div>
        )}
      </section>

      <section className="glass-card space-y-6 p-6">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Billing</h2>
          <p className="text-sm text-muted-foreground">Defaults applied to newly provisioned organisations.</p>
        </div>
        {isLoading ? (
          <div className="space-y-3">
            {[...Array(2).keys()].map((key) => (
              <div key={key} className="h-12 animate-pulse rounded-xl bg-white/40 dark:bg-white/10" />
            ))}
          </div>
        ) : (
          <div className="grid gap-5 md:grid-cols-2">
            <label className="flex flex-col gap-2 text-sm font-medium text-foreground">
              Default plan
              <select
                value={billing.defaultPlan}
                onChange={(event) => setBilling((prev) => ({ ...prev, defaultPlan: event.target.value }))}
                className="focus-ring rounded-xl border border-white/20 bg-white/60 px-3 py-2 text-sm text-foreground dark:bg-white/5"
              >
                <option value="starter">Starter</option>
                <option value="pro">Pro</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </label>
            <label className="flex flex-col gap-2 text-sm font-medium text-foreground">
              Trial length (days)
              <input
                type="number"
                min={0}
                value={billing.trialLengthDays}
                onChange={(event) => setBilling((prev) => ({ ...prev, trialLengthDays: Number(event.target.value) }))}
                className="focus-ring rounded-xl border border-white/20 bg-white/60 px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground dark:bg-white/5"
              />
            </label>
          </div>
        )}
      </section>

      <div className="flex justify-end gap-3">
        <button
          type="button"
          className="btn-glass btn-secondary focus-ring text-sm font-semibold"
          onClick={() => {
            if (data?.branding) {
              setBranding({
                productName: String(data.branding.productName ?? ''),
                supportEmail: String(data.branding.supportEmail ?? ''),
                marketingSite: String(data.branding.marketingSite ?? ''),
              });
            }
            if (data?.billing) {
              setBilling({
                defaultPlan: String(data.billing.defaultPlan ?? 'starter'),
                trialLengthDays: Number(data.billing.trialLengthDays ?? 7),
              });
            }
          }}
          disabled={!hasChanges || saving}
        >
          Reset
        </button>
        <button
          type="button"
          className="btn-glass btn-primary focus-ring text-sm font-semibold"
          onClick={handleSave}
          disabled={!hasChanges || saving}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}

